// frontend/src/pages/admin/BankAccounts.js
import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiDollarSign,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiCheck,
  FiX,
  FiAlertCircle
} from 'react-icons/fi';

const BankAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    bank_name: '',
    account_number: '',
    account_name: '',
    transaction_template: '',
    description: '',
    sort_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  const fetchBankAccounts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/bank-accounts');
      
      if (data.success) {
        setAccounts(data.data.accounts);
      }
    } catch (error) {
      console.error('Fetch bank accounts error:', error);
      toast.error('Банкны данс татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (account = null) => {
    if (account) {
      setEditMode(true);
      setSelectedAccount(account);
      setFormData({
        bank_name: account.bank_name,
        account_number: account.account_number,
        account_name: account.account_name,
        transaction_template: account.transaction_template,
        description: account.description || '',
        sort_order: account.sort_order,
        is_active: account.is_active === 1
      });
    } else {
      setEditMode(false);
      setSelectedAccount(null);
      setFormData({
        bank_name: '',
        account_number: '',
        account_name: '',
        transaction_template: '',
        description: '',
        sort_order: 0,
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedAccount(null);
    setFormData({
      bank_name: '',
      account_number: '',
      account_name: '',
      transaction_template: '',
      description: '',
      sort_order: 0,
      is_active: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.bank_name || !formData.account_number || !formData.account_name || !formData.transaction_template) {
      toast.error('Банкны нэр, дансны дугаар, дансны нэр, гүйлгээний утга заавал бөглөнө үү');
      return;
    }

    try {
      if (editMode) {
        // Update
        const { data } = await axios.put(`/api/bank-accounts/${selectedAccount.id}`, formData);
        
        if (data.success) {
          toast.success('Банкны данс амжилттай шинэчлэгдлээ');
          fetchBankAccounts();
          handleCloseModal();
        }
      } else {
        // Create
        const { data } = await axios.post('/api/bank-accounts', formData);
        
        if (data.success) {
          toast.success('Банкны данс амжилттай нэмэгдлээ');
          fetchBankAccounts();
          handleCloseModal();
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.message || 'Алдаа гарлаа');
    }
  };

  const handleToggleActive = async (account) => {
    try {
      const { data } = await axios.put(`/api/bank-accounts/${account.id}/toggle`);
      
      if (data.success) {
        toast.success('Төлөв өөрчлөгдлөө');
        fetchBankAccounts();
      }
    } catch (error) {
      console.error('Toggle error:', error);
      toast.error('Төлөв өөрчлөхөд алдаа гарлаа');
    }
  };

  const handleDelete = (account) => {
    setDeleteTarget(account);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const { data } = await axios.delete(`/api/bank-accounts/${deleteTarget.id}`);
      
      if (data.success) {
        toast.success('Банкны данс амжилттай устгагдлаа');
        fetchBankAccounts();
        setShowDeleteConfirm(false);
        setDeleteTarget(null);
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FiDollarSign className="mr-2" />
            Банкны данс
          </h1>
          <p className="text-gray-600 mt-1">Банкны дансны удирдлага</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn btn-primary"
        >
          <FiPlus className="mr-2" size={18} />
          Шинэ данс нэмэх
        </button>
      </div>

      {/* Accounts List */}
      <div className="card">
        {loading ? (
          <div className="text-center py-12">
            <div className="spinner-lg mx-auto mb-4"></div>
            <p className="text-gray-600">Уншиж байна...</p>
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-12">
            <FiDollarSign size={64} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600 text-lg">Банкны данс бүртгэгдээгүй байна</p>
            <p className="text-gray-500 text-sm mt-2">Эхний дансаа нэмнэ үү</p>
            <button
              onClick={() => handleOpenModal()}
              className="btn btn-primary mt-4"
            >
              <FiPlus className="mr-2" size={18} />
              Данс нэмэх
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Эрэмбэ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Банкны нэр
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Дансны дугаар
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Дансны нэр
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Гүйлгээний утга
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Төлөв
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Үйлдэл
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accounts.map((account) => (
                  <tr key={account.id} className={account.is_active ? '' : 'bg-gray-50 opacity-60'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{account.sort_order}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <FiDollarSign className="text-blue-600" size={20} />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{account.bank_name}</div>
                          {account.description && (
                            <div className="text-xs text-gray-500">{account.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-mono text-gray-900">{account.account_number}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">{account.account_name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{account.transaction_template}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleToggleActive(account)}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          account.is_active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {account.is_active ? (
                          <>
                            <FiEye className="mr-1" size={14} />
                            Идэвхтэй
                          </>
                        ) : (
                          <>
                            <FiEyeOff className="mr-1" size={14} />
                            Идэвхгүй
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenModal(account)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Засах"
                        >
                          <FiEdit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(account)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          title="Устгах"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editMode ? 'Банкны данс засах' : 'Шинэ данс нэмэх'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-4">
                {/* Bank Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Банкны нэр <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    className="input"
                    placeholder="Жишээ: Хаан банк"
                    required
                  />
                </div>

                {/* Account Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Дансны дугаар <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    className="input font-mono"
                    placeholder="Жишээ: 5123456789"
                    required
                  />
                </div>

                {/* Account Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Дансны нэр <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.account_name}
                    onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                    className="input"
                    placeholder="Жишээ: Б.Батаа"
                    required
                  />
                </div>

                {/* Transaction Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Гүйлгээний утга <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.transaction_template}
                    onChange={(e) => setFormData({ ...formData, transaction_template: e.target.value })}
                    className="input"
                    placeholder="Жишээ: Захиалгын дугаар + Утас"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Харилцагч төлбөр шилжүүлэхдээ ашиглах утга
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Тайлбар
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input"
                    rows={3}
                    placeholder="Нэмэлт мэдээлэл (заавал биш)"
                  />
                </div>

                {/* Sort Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Эрэмбэ
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    className="input"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Бага тоо эхэнд харагдана
                  </p>
                </div>

                {/* Active Status */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                    Идэвхтэй
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn btn-secondary"
                >
                  Цуцлах
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  <FiCheck className="mr-2" size={18} />
                  {editMode ? 'Хадгалах' : 'Нэмэх'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <FiAlertCircle className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Данс устгах
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Та итгэлтэй байна уу?
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                <span className="font-medium">{deleteTarget.bank_name}</span> - {deleteTarget.account_number}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Энэ үйлдлийг буцаах боломжгүй
              </p>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteTarget(null);
                }}
                className="btn btn-secondary"
              >
                Цуцлах
              </button>
              <button
                onClick={confirmDelete}
                className="btn btn-danger"
              >
                <FiTrash2 className="mr-2" size={18} />
                Устгах
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankAccounts;